import { List, LayoutGrid } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../components/ui/select";
import {
	ToggleGroup,
	ToggleGroupItem,
} from "../../../components/ui/toggle-group";
import {
	ArchiveSort,
	ArchiveViewMode,
} from "../../../store/Archive/archive.store";

const SORT_OPTIONS: { value: ArchiveSort; label: string }[] = [
	{ value: "date_desc", label: "Neueste zuerst" },
	{ value: "date_asc", label: "Älteste zuerst" },
	{ value: "name_asc", label: "Name A–Z" },
	{ value: "name_desc", label: "Name Z–A" },
];

interface ArchiveControlsProps {
	filterYear: number | null;
	filterKinds: string[];
	search: string;
	sort: ArchiveSort;
	viewMode: ArchiveViewMode;
	isFlat: boolean;
	canGroup: boolean;
	onReset: () => void;
	onSortChange: (sort: ArchiveSort) => void;
	onViewModeChange: (mode: ArchiveViewMode) => void;
}

export const ArchiveControls: React.FC<ArchiveControlsProps> = ({
	filterYear,
	filterKinds,
	search,
	sort,
	viewMode,
	isFlat,
	canGroup,
	onReset,
	onSortChange,
	onViewModeChange,
}) => {
	const hasActiveFilter =
		filterYear !== null || filterKinds.length > 0 || search.trim() !== "";

	return (
		<div className="ml-auto flex flex-wrap items-center gap-2">
			{hasActiveFilter && (
				<Button
					variant="ghost"
					size="sm"
					className="h-8 px-2 text-muted-foreground"
					onClick={onReset}
					aria-label="Alle Filter zurücksetzen"
					data-testid="archive-reset-filters"
				>
					Filter zurücksetzen
				</Button>
			)}

			<div className="flex items-center gap-2">
				{isFlat && (
					<Select
						value={sort}
						onValueChange={(value) =>
							onSortChange(value as ArchiveSort)
						}
					>
						<SelectTrigger
							className="h-8 w-40"
							aria-label="Sortierung"
							data-testid="archive-sort"
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{SORT_OPTIONS.map((option) => (
								<SelectItem
									key={option.value}
									value={option.value}
								>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}

				{canGroup && search.trim() === "" && (
					<ToggleGroup
						type="single"
						variant="outline"
						size="sm"
						value={viewMode}
						onValueChange={(value) =>
							value && onViewModeChange(value as ArchiveViewMode)
						}
						aria-label="Ansicht"
					>
						<ToggleGroupItem
							value="grouped"
							aria-label="Nach Auftrag gruppiert"
							data-testid="archive-view-grouped"
						>
							<LayoutGrid className="mr-1 h-4 w-4" />
							Aufträge
						</ToggleGroupItem>
						<ToggleGroupItem
							value="flat"
							aria-label="Als flache Liste"
							data-testid="archive-view-flat"
						>
							<List className="mr-1 h-4 w-4" />
							Liste
						</ToggleGroupItem>
					</ToggleGroup>
				)}
			</div>
		</div>
	);
};
